import React, { useEffect, useMemo, useState } from 'react';
import {
  Receipt, Search, RefreshCw, Filter, Copy, Check, Loader2,
  AlertTriangle, ArrowDownToLine, CreditCard, Wallet, Clock,
} from 'lucide-react';
import { Card } from '../components/Card';
import { SectionHeader } from '../components/Tabs';
import { Badge, Dot } from '../components/Badge';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { EmptyState, Spinner } from '../components/Dialog';
import { AreaChart } from '../components/AreaChart';
import { OperatorStat } from '../components/OperatorStat';
import { adminFetch } from '../adminApi';
import { cn, formatNumber, formatRelative, formatUSD } from '../lib/utils';

const STATUS_TABS = [
  { value: 'all',       label: 'All' },
  { value: 'finished',  label: 'Finished' },
  { value: 'pending',   label: 'Pending' },
  { value: 'failed',    label: 'Failed / Expired' },
];

/**
 * PaymentsPage — operator-side view of every checkout attempt.
 *
 * Tabs by status, search by email + plan, copy payment id, view breakdown.
 * All data is real (no hard-coded placeholders).
 */
export default function PaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [search, setSearch] = useState('');

  async function load() {
    setLoading(true); setError(null);
    try {
      const data = await adminFetch('/admin/payments?limit=300');
      setPayments(data.payments || []);
    } catch (e) {
      setError(e.message || 'failed_to_load');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { finished: 0, pending: 0, failed: 0, expired: 0 };
    for (const p of payments) {
      if (p.status === 'finished') c.finished++;
      else if (p.status === 'pending') c.pending++;
      else if (p.status === 'failed') c.failed++;
      else if (p.status === 'expired') c.expired++;
    }
    return c;
  }, [payments]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return payments.filter((p) => {
      if (statusFilter !== 'all') {
        if (statusFilter === 'failed' && p.status !== 'failed' && p.status !== 'expired') return false;
        if (statusFilter !== 'failed' && p.status !== statusFilter) return false;
      }
      if (kindFilter !== 'all' && p.kind !== kindFilter) return false;
      if (needle && !p.email.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [payments, statusFilter, kindFilter, search]);

  // Stats
  const totalCents = payments.filter((p) => p.status === 'finished').reduce((s, p) => s + (p.amount_cents || 0), 0);
  const last30 = Date.now() - 30 * 86400 * 1000;
  const recent = payments.filter((p) => new Date(p.created_at).getTime() > last30 && p.status === 'finished').reduce((s, p) => s + (p.amount_cents || 0), 0);
  const pending = payments.filter((p) => p.status === 'pending');
  const pendingCents = pending.reduce((s, p) => s + (p.amount_cents || 0), 0);
  const conversions = payments.length === 0 ? 0 : (counts.finished / payments.length) * 100;

  // Revenue by day (last 30 days)
  const revenueByDay = useMemo(() => {
    const days = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      d.setHours(0, 0, 0, 0);
      const ts = d.getTime();
      const next = ts + 86400 * 1000;
      const cents = payments
        .filter((p) => p.status === 'finished'
          && new Date(p.created_at).getTime() >= ts
          && new Date(p.created_at).getTime() < next)
        .reduce((s, p) => s + (p.amount_cents || 0), 0);
      days.push({ ts, cents });
    }
    return days;
  }, [payments]);

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Payments"
        description="Every checkout attempt — subscriptions and top-ups — across all customers."
        actions={
          <Button variant="secondary" size="sm" onClick={load}>
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <OperatorStat
          icon={CreditCard}
          label="Revenue (all-time)"
          value={loading ? null : formatUSD(totalCents)}
          hint={loading ? 'Loading…' : `${formatNumber(counts.finished)} finished`}
          tone="brand"
          loading={loading}
        />
        <OperatorStat
          icon={Wallet}
          label="Revenue (30d)"
          value={loading ? null : formatUSD(recent)}
          hint={loading ? 'Loading…' : 'finished in last 30d'}
          tone="success"
          loading={loading}
        />
        <OperatorStat
          icon={Clock}
          label="Pending"
          value={loading ? null : formatUSD(pendingCents)}
          hint={loading ? 'Loading…' : `${formatNumber(pending.length)} awaiting confirmation`}
          tone={pending.length ? 'warning' : 'neutral'}
          loading={loading}
        />
        <OperatorStat
          icon={ArrowDownToLine}
          label="Conversion"
          value={loading ? null : `${conversions.toFixed(1)}%`}
          hint={loading ? 'Loading…' : `${counts.finished} of ${formatNumber(payments.length)} finished`}
          tone={conversions >= 80 ? 'success' : conversions >= 50 ? 'warning' : 'destructive'}
          loading={loading}
        />
      </div>

      {/* Revenue chart */}
      <Card>
        <Card.Header>
          <div>
            <Card.Title>Revenue (30d)</Card.Title>
            <Card.Subtitle>Daily totals from finished payments.</Card.Subtitle>
          </div>
        </Card.Header>
        <Card.Body>
          <AreaChart
            data={revenueByDay.map((d) => ({ ts: d.ts, value: d.cents / 100 }))}
            valueKey="value"
            height={140}
            loading={loading}
            emptyText="No revenue yet."
            formatY={(v) => formatUSD(v)}
          />
        </Card.Body>
      </Card>

      {/* Filters */}
      <Card>
        <Card.Body className="p-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by email…"
                className="pl-9"
              />
            </div>
            <select
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value)}
              className="h-9 px-3 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
            >
              <option value="all">All kinds</option>
              <option value="subscription">Subscription</option>
              <option value="topup">Top-up</option>
            </select>
            <Badge variant="default">{filtered.length} / {payments.length}</Badge>
          </div>

          {/* Status tabs */}
          <div className="mt-3 inline-flex flex-wrap gap-1 p-1 rounded-lg bg-surface-2 border border-border">
            {STATUS_TABS.map((t) => {
              const count = t.value === 'all' ? payments.length
                : t.value === 'failed' ? counts.failed + counts.expired
                : counts[t.value] || 0;
              const active = statusFilter === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setStatusFilter(t.value)}
                  className={cn(
                    'px-2.5 py-1 rounded-md text-xs font-medium transition-colors',
                    active ? 'bg-surface text-fg shadow-sm' : 'text-fg-muted hover:text-fg'
                  )}
                >
                  {t.label}
                  <span className={cn(
                    'ml-1.5 px-1.5 py-0.5 rounded text-2xs',
                    active ? 'bg-brand-softest text-brand-strong' : 'bg-surface text-fg-subtle'
                  )}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card>
        <Card.Body className="p-0">
          {error ? (
            <div className="p-6 text-sm text-destructive flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> {error}
            </div>
          ) : loading ? (
            <div className="p-8 flex items-center justify-center"><Spinner size={20} /></div>
          ) : filtered.length === 0 ? (
            <div className="p-8">
              <EmptyState
                icon={Receipt}
                title="No payments"
                description={search || statusFilter !== 'all' ? 'Try adjusting your filters.' : 'Payment attempts will appear here.'}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border bg-surface-2/30">
                  <tr className="text-2xs font-medium text-fg-muted uppercase tracking-wider">
                    <th className="text-left px-5 py-2.5">Customer</th>
                    <th className="text-left px-5 py-2.5">Kind</th>
                    <th className="text-left px-5 py-2.5">Plan / credits</th>
                    <th className="text-right px-5 py-2.5">Amount</th>
                    <th className="text-left px-5 py-2.5">Status</th>
                    <th className="text-left px-5 py-2.5">Crypto</th>
                    <th className="text-left px-5 py-2.5">When</th>
                    <th className="text-right px-5 py-2.5">ID</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.id} className="border-b border-border last:border-b-0 hover:bg-surface-2/40">
                      <td className="px-5 py-3 text-sm text-fg font-mono truncate max-w-[220px]">{p.email}</td>
                      <td className="px-5 py-3">
                        <Badge variant={p.kind === 'subscription' ? 'info' : 'default'} className="text-2xs">
                          {p.kind === 'subscription' ? <CreditCard className="h-3 w-3" /> : <Wallet className="h-3 w-3" />}
                          {p.kind}
                        </Badge>
                      </td>
                      <td className="px-5 py-3 text-xs text-fg-muted">
                        {p.plan_code ? <span className="font-mono">{p.plan_code}</span> : '—'}
                        {p.credits_granted ? <span className="text-fg-subtle"> · {formatNumber(p.credits_granted)} cr</span> : null}
                      </td>
                      <td className="px-5 py-3 text-sm text-fg text-right tabular-nums">{formatUSD(p.amount_cents || 0)}</td>
                      <td className="px-5 py-3"><StatusBadge status={p.status} /></td>
                      <td className="px-5 py-3 text-xs text-fg-muted font-mono">
                        {p.pay_currency ? (
                          <>
                            <span>{p.pay_currency}</span>
                            {p.pay_amount && <span className="text-fg-subtle"> · {p.pay_amount}</span>}
                          </>
                        ) : '—'}
                      </td>
                      <td className="px-5 py-3 text-xs text-fg-muted">{formatRelative(p.created_at)}</td>
                      <td className="px-5 py-3 text-right">
                        <CopyId id={p.id} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = {
    finished: { variant: 'success', label: 'Finished', icon: Check },
    pending:  { variant: 'warning', label: 'Pending',  icon: Clock },
    failed:   { variant: 'destructive', label: 'Failed', icon: AlertTriangle },
    expired:  { variant: 'destructive', label: 'Expired', icon: AlertTriangle },
  }[status] || { variant: 'default', label: status, icon: Clock };
  const Icon = meta.icon;
  return (
    <Badge variant={meta.variant} className="text-2xs">
      <Dot variant={meta.variant} pulse={status === 'pending'} />
      <Icon className="h-3 w-3" />
      {meta.label}
    </Badge>
  );
}

function CopyId({ id }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  }
  return (
    <button
      type="button"
      onClick={copy}
      className="text-2xs text-fg-muted hover:text-fg font-mono inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-surface-2"
      title={id}
    >
      <span>{id.slice(0, 8)}…</span>
      {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3 opacity-50" />}
    </button>
  );
}
